import { useEffect, useMemo, useRef } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import L, { latLngBounds } from "leaflet";
import type { LatLngExpression } from "leaflet";
import { containerService } from "@/data/mock-containers";
import type { Container } from "@/types/container";
import { generateRealisticRoute } from "@/utils/route-generator";

const ROUTE_COLORS = [
    "#2563eb",
    "#16a34a",
    "#db2777",
    "#f59e0b",
    "#7c3aed",
    "#059669",
    "#dc2626",
    "#0ea5e9",
];

function interpolate(a: [number, number], b: [number, number], t: number): [number, number] {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function FitBounds({ points }: { points: [number, number][] }) {
    const map = useMap();
    useEffect(() => {
        if (!points.length) return;
        const bounds = latLngBounds(points);
        const timer = setTimeout(() => {
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 4 });
        }, 50);
        return () => clearTimeout(timer);
    }, [map, JSON.stringify(points)]);
    return null;
}

export function WorldRoutesMapPreview({ height = 360, role = 'shipper', accountId: explicitId }: { height?: number; role?: 'shipper' | 'receiver' | 'freight-forwarder'; accountId?: string }) {
    const fallbackId = role === 'shipper' ? '1' : role === 'receiver' ? '2' : '3';
    const accountId = explicitId || ((typeof window !== 'undefined' && localStorage.getItem('accountId')) || fallbackId);
    const containers: Container[] = useMemo(() => {
        const all = containerService.getAll();
        if (role === 'shipper') {
            return all.filter(c => c.shipperId === accountId);
        } else if (role === 'receiver') {
            return all.filter(c => c.receiverId === accountId);
        } else {
            // Freight Forwarder: mostra container con bookingNumber
            return all.filter(c => c.bookingNumber !== undefined && c.bookingNumber !== null && c.bookingNumber !== '');
        }
    }, [accountId, role]);

    const routes = useMemo(() => {
        return containers
            .map((c, idx) => {
                const waypoints = c.waypoints && c.waypoints.length > 0
                    ? c.waypoints
                    : generateRealisticRoute(c.route.from, c.route.to, c.createdAt);
                if (waypoints.length === 0) return null;
                const coordinates = waypoints.map(w => [w.lat, w.lng] as [number, number]);
                const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
                return { c, coordinates, color } as const;
            })
            .filter(Boolean) as Array<{ c: Container; coordinates: [number, number][]; color: string }>;
    }, [containers]);

    const allPoints = useMemo(() => routes.flatMap(r => r.coordinates), [routes]);

    const mapDivRef = useRef<HTMLDivElement | null>(null);

    return (
        <div ref={mapDivRef} style={{ height, width: '100%' }} className="rounded-xl overflow-hidden ring-1 ring-secondary bg-secondary">
            <MapContainer style={{ height: "100%", width: "100%" }} center={[20, 0]} zoom={2} worldCopyJump scrollWheelZoom>
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* Route lines rimosse - mostrare solo marker */}

                {routes.map(({ c, coordinates, color }) => {
                    // posizione a ~50% della rotta per preview
                    let totalDistance = 0;
                    const segmentDistances: number[] = [];
                    for (let i = 0; i < coordinates.length - 1; i++) {
                        const dLat = coordinates[i + 1][0] - coordinates[i][0];
                        const dLng = coordinates[i + 1][1] - coordinates[i][1];
                        const dist = Math.sqrt(dLat * dLat + dLng * dLng);
                        segmentDistances.push(dist);
                        totalDistance += dist;
                    }
                    let shipPos: LatLngExpression = coordinates[0];
                    if (coordinates.length >= 2 && totalDistance > 0) {
                        const targetDistance = 0.5 * totalDistance;
                        let acc = 0;
                        for (let i = 0; i < segmentDistances.length; i++) {
                            const seg = segmentDistances[i];
                            if (seg === 0) continue;
                            if (acc + seg >= targetDistance) {
                                const segmentT = (targetDistance - acc) / seg;
                                shipPos = interpolate(coordinates[i], coordinates[i + 1], segmentT) as LatLngExpression;
                                break;
                            }
                            acc += seg;
                            if (i === segmentDistances.length - 1) shipPos = coordinates[coordinates.length - 1];
                        }
                    }
                    const [lat, lng] = shipPos as [number, number];
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

                    const icon = L.divIcon({
                        html: `<svg width="20" height="24" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg"><path d="M16 0C7.163 0 0 7.163 0 16C0 24.837 16 40 16 40C16 40 32 24.837 32 16C32 7.163 24.837 0 16 0Z" fill="${color}"/><circle cx="16" cy="16" r="5" fill="white"/></svg>`,
                        className: "",
                        iconSize: [20, 24],
                        iconAnchor: [10, 24],
                    });
                    return <Marker key={`marker-${c.id}`} position={shipPos} icon={icon} />;
                })}

                <FitBounds points={allPoints} />
            </MapContainer>
        </div>
    );
}

export default WorldRoutesMapPreview;

